import { AddRounded, RemoveRounded } from '@mui/icons-material'
import { Box, Grid, IconButton, InputBase, Typography } from '@mui/material'
import { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo } from 'react'

export type Props = {
    price: number
    setSpotPriceForProfitCalc: Dispatch<SetStateAction<number>>
    priceThreshold: number
    setPriceThreshold: Dispatch<SetStateAction<number>>
}

const SpotPriceForProfitCalcView = ({ price, setSpotPriceForProfitCalc, priceThreshold, setPriceThreshold }: Props) => {
    const spotPriceForProfitCalculation = useMemo(() => price + priceThreshold, [price, priceThreshold])

    const spotPriceForProfitMinusBtnColor = useMemo(() => (spotPriceForProfitCalculation - 10 > 0 ? 'inherit' as const : undefined), [spotPriceForProfitCalculation])

    const spotPriceForProfitMinusDisabled = useMemo(() => spotPriceForProfitCalculation - 10 <= 0, [spotPriceForProfitCalculation])

    const handleSpotPriceForProfitPlusClick = useCallback(() => {
        setPriceThreshold(priceThreshold + 10)
    }, [priceThreshold, setPriceThreshold])

    const handleSpotPriceForProfitMinusClick = useCallback(() => {
        if (spotPriceForProfitCalculation - 10 > 0) {
            setPriceThreshold(priceThreshold - 10)
        }
    }, [priceThreshold, setPriceThreshold, spotPriceForProfitCalculation])

    useEffect(() => {
        setSpotPriceForProfitCalc(spotPriceForProfitCalculation)
    }, [spotPriceForProfitCalculation])

    const spotPriceForProfitCalculationValue = useMemo(() => spotPriceForProfitCalculation.toFixed(2), [spotPriceForProfitCalculation])

    const priceText = useMemo(
        () => (
            <Typography sx={{ fontSize: '10px' }}>
                PRICE
            </Typography>
        ),
        []
    )
    const minusIcon = useMemo(() => <RemoveRounded className="trade-panel-edit-icons" />, [])
    const plusIcon = useMemo(() => <AddRounded className="trade-panel-edit-icons" />, [])

    const minusButton = useMemo(() => {
        return (
            <IconButton
                data-event={'Spot Price Changed'} data-event-source={'strategy-builder-submenu'} data-event-type={'decrement'}
                className="order-buttons"
                sx={{ padding: '5px', cursor: 'pointer', width: '24px', height: '24px' }}
                color={spotPriceForProfitMinusBtnColor}
                disabled={spotPriceForProfitMinusDisabled}
                onClick={handleSpotPriceForProfitMinusClick}
            >
                {minusIcon}
            </IconButton>
        )
    }, [spotPriceForProfitMinusBtnColor, spotPriceForProfitMinusDisabled, handleSpotPriceForProfitMinusClick, minusIcon])

    const plusButton = useMemo(() => {
        return (
            <IconButton data-event={'Spot Price Changed'} data-event-source={'strategy-builder-submenu'} data-event-type={'increment'} className="order-buttons" style={{ width: '24px', height: '24px' }} sx={{ padding: '5px', width: '24px', height: '24px', cursor: 'pointer' }} color={'inherit'} onClick={handleSpotPriceForProfitPlusClick}>
                {plusIcon}
            </IconButton>
        )
    }, [handleSpotPriceForProfitPlusClick, plusIcon])

    const buttonsView = useMemo(() => {
        return (
            <Box
                component="span"
                sx={{
                    height: '24px',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                }}
            >
                {minusButton}
                <InputBase
                    value={spotPriceForProfitCalculationValue}
                    className="order-custom-input"
                    sx={{
                        height: '100%',
                        textAlign: 'center',
                        backgroundColor: 'none',
                        width: '80px',
                        fontSize: '14px',
                        color: 'inherit',
                        '& .MuiInputBase-input': { padding: '4px 0 5px' }
                    }}
                />
                {plusButton}
            </Box>
        )
    }, [minusButton, plusButton, spotPriceForProfitCalculationValue])

    const jsxTree = useMemo(() => {
        return (
            <Grid item id={'spot-price-input'} sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                <Grid item xs={12} textAlign={'left'}>
                    <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                        {priceText}
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography className="trade-panel-boldest-text" variant="body2" component="span" color="primaryText" fontSize={'small'} sx={{ fontSize: '10px' }}>
                        {buttonsView}
                    </Typography>
                </Grid>
                {/* <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', transform: 'translateX(40%)' }}> */}
            </Grid>
        )
    }, [priceText, buttonsView])

    return (jsxTree)
}

export default memo(SpotPriceForProfitCalcView)
